/**
 * Source Service
 * Handles source-related API calls
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { SourceAdapter } from '@/adapters';
import type {
  ApiResponse,
  Source,
  CreateSourceDto,
  UpdateSourceDto,
  SourceResponseDto,
  SourcesListResponseDto,
  SourceStats,
  SourceStatsResponseDto,
  SourcesListQueryParams
} from '@/types';

export const sourceService = {
  /**
   * Get all sources. Unbounded (no page/limit sent) — used by pickers/accordions that
   * genuinely need the full list, not just one page of it.
   */
  async getSources(params?: { categrizationId?: string }) {
    const response = await api.get<ApiResponse<SourcesListResponseDto>>(API_ENDPOINTS.SOURCES.BASE, { params });
    return SourceAdapter.fromApiList(response.data.data.sources);
  },

  /**
   * Paginated counterpart used by the Sources/Knowledge table — sending page/limit
   * switches the backend into its paginated response shape (adds page/totalPages).
   */
  async getSourcesPaginated(params: SourcesListQueryParams): Promise<{
    sources: Source[];
    pagination: { page: number; totalPages: number; total: number };
  }> {
    const response = await api.get<ApiResponse<SourcesListResponseDto>>(API_ENDPOINTS.SOURCES.BASE, {
      params: SourceAdapter.toListQuery(params),
    });
    return SourceAdapter.fromListApi(response.data.data);
  },

  /**
   * Get user's sources
   */
  async getMySources(params?: { bu_id?: string }) {
    const response = await api.get<ApiResponse<SourcesListResponseDto>>(API_ENDPOINTS.SOURCES.MY_SOURCES, { params });
    return SourceAdapter.fromApiList(response.data.data.sources);
  },

  /**
   * Get source by ID
   */
  async getSourceById(id: string) {
    const response = await api.get<ApiResponse<SourceResponseDto>>(API_ENDPOINTS.SOURCES.BY_ID(id));
    return SourceAdapter.fromApi(response.data.data);
  },

  /**
   * Get a source's readable text content (ARTICLE's raw value, or LINK's
   * extracted text stitched from Pinecone chunks)
   */
  async getSourceContent(id: string): Promise<{ content: string; origin: 'value' | 'pinecone' }> {
    const response = await api.get<ApiResponse<{ content: string; origin: 'value' | 'pinecone' }>>(
      API_ENDPOINTS.SOURCES.CONTENT(id)
    );
    return response.data.data;
  },

  /**
   * Export a Google Doc/Sheet source to a viewable PDF/XLSX preview URL
   */
  async getExportPreview(id: string): Promise<{ previewUrl: string; mimeType: string }> {
    const response = await api.get<ApiResponse<{ previewUrl: string; mimeType: string }>>(
      API_ENDPOINTS.SOURCES.EXPORT_PREVIEW(id)
    );
    return response.data.data;
  },

  /**
   * Get source statistics
   */
  async getStats(params?: { bu_id?: string }): Promise<SourceStats> {
    const response = await api.get<ApiResponse<SourceStatsResponseDto>>(API_ENDPOINTS.SOURCES.STATS, { params });
    return SourceAdapter.fromStatsApi(response.data.data);
  },

  /**
   * Create new source
   */
  async createSource(data: CreateSourceDto, scope: { bu_id?: string } = {}): Promise<Source> {
    const payload = SourceAdapter.toCreateRequest(data);
    const response = await api.post<ApiResponse<SourceResponseDto>>(
      withBuScope(API_ENDPOINTS.SOURCES.BASE, scope),
      payload
    );
    return SourceAdapter.fromApi(response.data.data);
  },

  /**
   * Update source
   */
  async updateSource(id: string, data: UpdateSourceDto): Promise<Source> {
    const payload = SourceAdapter.toUpdateRequest(data);
    const response = await api.put<ApiResponse<SourceResponseDto>>(API_ENDPOINTS.SOURCES.BY_ID(id), payload);
    return SourceAdapter.fromApi(response.data.data);
  },

  /**
   * Delete source
   */
  async deleteSource(id: string): Promise<void> {
    await api.delete<ApiResponse<null>>(API_ENDPOINTS.SOURCES.BY_ID(id));
  },

  /**
   * Trigger a manual resync for a Google integration source
   */
  async resyncSource(id: string): Promise<{ status: 'unchanged' | 'queued' }> {
    const response = await api.post<ApiResponse<{ status: 'unchanged' | 'queued' }>>(
      API_ENDPOINTS.SOURCES.RESYNC(id)
    );
    return response.data.data;
  },

  /**
   * Assign a source to a category/subcategory (find-or-create)
   */
  async updateCategorization(id: string, category: string, subcategory: string): Promise<Source> {
    const response = await api.patch<ApiResponse<SourceResponseDto>>(API_ENDPOINTS.SOURCES.CATEGORIZATION(id), {
      category,
      subcategory
    });
    return SourceAdapter.fromApi(response.data.data);
  },

  /** File every uncategorized source under a category from the intent catalog. */
  async autoCategorize(scope: { bu_id?: string } = {}): Promise<{ scanned: number; categorized: number }> {
    const response = await api.post<ApiResponse<{ scanned: number; categorized: number }>>(
      withBuScope(API_ENDPOINTS.SOURCES.AUTO_CATEGORIZE, scope),
      {},
      // One model call per item, a batch of up to 50 — longer than the default timeout.
      { timeout: 5 * 60 * 1000 }
    );
    return response.data.data;
  }
};
